/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {EEmbededElementTypes, StorageUtils, RenderDocument} from "js_renderer";
import Element from "./element";
import {vec2, vec3} from "gl-matrix";
import Factory from "./factory";
import IPoint from "./point_interface";

export default
class Point extends Element implements IPoint {

    public static TypeName: string = EEmbededElementTypes.POINT;
    public static texture_size: number[] = [256, 256];

    protected _position: vec3;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._position = vec3.create();

        let url = '@assets/images/points_texture.png';

        // glyph 16 x 16 px in a texture [256 x 256] px
        this.extendProperties({
            minPointSize: 12.0,
            maxPointSize: 12.0,
            glyphPosition: [0.0 / Point.texture_size[0], 0 / Point.texture_size[1]],
            glyphSize: [16.0 / Point.texture_size[0], 16.0 / Point.texture_size[1]],
            alwaysOnTop: false,
            pickable: true,
            textureUrl: url,
            textureSize: vec2.clone(Point.texture_size),
            textureHasAlpha: true,
            pinPoint: [0.0, 0.0]
        });

    }

    public static create(aRenderDocument: RenderDocument): Point {
        let element = new Point(aRenderDocument);
        element._typeName = Point.TypeName;
        return element;
    }

    /**
     * Set point position in local coords [x, z, -y] ?
     * @param aPosition
     */
    public setPosition(aPosition: vec3) {
        if (aPosition.slice !== undefined) {
            this._position = aPosition.slice();
        } else {
            vec3.copy(this._position, aPosition);
        }

        this.updatePositions([
            new Float32Array([this._position[0], this._position[1], this._position[2]])
        ]);
    }

    /***
     * Set an offset by which the rendered point will be moved within the rendered point size. The range of the input value is [0..1, 0..1].
     * @param {vec2} aValue For example if we set the offset to [0.0, 0.5] then we move the center pixel up by half of the point size
     * resulting to the state that the bottom pixel of the point will be rendered at the position of the point.
     */
    public setPinPoint(aValue: vec2) {
        this._properties.pinPoint = vec2.clone(aValue);
    }

    /***
     * Set size of the point
     *
     * @param aSize
     * @param aMinSize
     * @param aMaxSize
     */
    public setSize(aMinSize: number, aMaxSize: number) {
        this._properties.minPointSize = aMinSize;
        this._properties.maxPointSize = aMaxSize;
    }

    public setTexture(aUrl: string) {
        this._properties.textureUrl = aUrl;
        this._properties.textureSize = vec2.create();
        this._properties.glyphPosition = [0.0, 0.0];
        this._properties.glyphSize = [1.0, 1.0];
    }

    /***
     * Animated zoom closer to the point.
     *
     * @param aFocusFactor A distance to which the camera will zoom in (a bounding box diameter * aFocusFactor)
     * @param aAnimationTime a Length of animated transition from current camera position to the focus point in seconds.
     */
    public focus(aFocusFactor: number, aAnimationTime: number) {
        this._sceneAPI.viewModeModule.focusToPosition(this._position, aFocusFactor, aAnimationTime);
    }

    /***
     * Keep the camera position on the tracked point and keep the target free to user (i.e. fps mode)
     * @param aAnimationTime
     * @param aHeight height offset over the followed point on terrain
     */
    public follow(aAnimationTime: number, aHeight: number) {
        this._sceneAPI.viewModeModule.goToPosition(this._position, aAnimationTime, aHeight);
    }

    /**
     * Return copy of the position in 3D scene.
     *
     * @returns {vec3}
     */
    public getPosition(): vec3 {
        return vec3.clone(this._position);
    }

    protected _generateElementDescriptionJSON(): any {
        return {
            "type": this.getTypeName(),
            "id": this.getTypeName() + "_" + this._UID,   // this is is not important in the engine, it can identify e.g. id of an element in Lexocad
            "UID": this._UID
        };
    }

    protected _generateGeometryObjectDescriptionJSON(): any {
        return [{
            description: {
                type: "POINT",
                bboxMin: [this._position[0], this._position[1], this._position[2]],
                bboxMax: [this._position[0], this._position[1], this._position[2]]
            },
            appearance: {
                pointProperties: {
                    minPointSize: this._properties.minPointSize,
                    maxPointSize: this._properties.maxPointSize,
                    glyphPos: this._properties.glyphPosition,
                    glyphSize: this._properties.glyphSize,
                    pinPoint: this._properties.pinPoint
                },
                renderOption: {
                    alwaysOnTop: this._properties.alwaysOnTop,
                    pickable: this._properties.pickable
                },
                imageTexture: {
                    url: [this._properties.textureUrl],
                    width: [this._properties.textureSize[0]],
                    height: [this._properties.textureSize[1]],
                    offsets: [0],
                    sizes: [0],
                    tiled: [false],
                    alpha: this._properties.textureHasAlpha,
                    model: "REPLACE",
                    blendColor: [1.0, 1.0, 1.0],
                    repeatS: false,
                    repeatT: false
                }
            },
            pointSet: {
                src: this._renderDocument.getRenderer()._dataStorage.createLocalResourceUri(),
                vertexCount: 1,
                binSize: 12,
                points: this._position
            }
        }];
    }
}

Factory.register(Point);
